// file: src/components/layout/RightChatPanel.jsx

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAuthContext } from '../../App';
import { useChatContext } from '../../context/ChatContext';
import { useTheme } from '../../context/ThemeContext';

import useDragWidth from '../../hooks/useDragWidth';
import useDialogDocStatus from '../../hooks/useDialogDocStatus';
import { Plus, MessageSquare, ChevronLeft, X, AlertTriangle, Edit, Copy, ArrowUp } from 'lucide-react';

import NewDialogModal from '../modals/NewDialogModal';
import ProtectedAction from '../common/ProtectedAction';

// Import our custom chat message component
import CustomChatMessage from '../CustomMarkdown/CustomChatMessage';

// Import the dialog details service
import { getDialogDetails } from '../../services/dialogServices'; 

// Import our specialized utility functions
import { getDialogDetails as getLocalDialogDetails } from '../../utils/localForageUtils';

import { sanitizeInternalTags } from '../../utils/citationService';

// Import the agent mode dropdown
import AgentModeDropdown from '../AgentModeDropdown';

// Import CSS module
import styles from './RightChatPanel.module.css';

import Banner from '../common/Banner';

/**
 * Helper function to detect mobile screen size
 */
function isMobileScreen() {
  return window.innerWidth < 768;
}

// Chat Message Component with improved styling
function ChatMessage({ message, onEdit, onCopy, isEditing, editInput, setEditInput, onEditSubmit, onEditCancel }) {
  const { theme } = useTheme();
  const { userProfile } = useAuthContext();
  const isUser = message.role === 'user';
  const [showActions, setShowActions] = React.useState(false);
  const hideTimeout = React.useRef(null);

  // Show actions on mouse enter, hide after 3s on leave
  const handleMouseEnter = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    setShowActions(true);
  };
  const handleMouseLeave = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    hideTimeout.current = setTimeout(() => setShowActions(false), 3000);
  };
  React.useEffect(() => () => { if (hideTimeout.current) clearTimeout(hideTimeout.current); }, []);

  // Copy functionality
  const handleCopy = () => {
    if (isUser && !isEditing) {
      navigator.clipboard.writeText(message.content);
      if (onCopy) onCopy(message);
    }
  };

  const getUserDisplayName = () => userProfile?.nickname || 'You';

  return (
    <div
      className={[
        styles.chatMessage,
        isUser ? styles.chatMessageUser : styles.chatMessageAssistant,
        isUser && isEditing ? 'editing' : ''
      ].join(' ')}
      onMouseEnter={isUser ? handleMouseEnter : undefined}
      onMouseLeave={isUser ? handleMouseLeave : undefined}
    >
      <div className={styles.chatMessageContent}>
        <div className={styles.chatMessageText}>
          {isUser && isEditing ? (
            <textarea
              className={styles.editBubbleTextarea}
              value={editInput}
              autoFocus
              onChange={e => setEditInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onEditSubmit();
                } else if (e.key === 'Escape') {
                  onEditCancel();
                }
              }}
              placeholder="Edit your message..."
              rows={1}
            />
          ) : (
            isUser ? <div>{message.content}</div> : (
              <CustomChatMessage 
                content={sanitizeInternalTags(message.content)}
                reference={message.reference}
              />
            )
          )}
          {/* Action buttons, only for user messages and not in edit mode */}
          {isUser && !isEditing && (
            <div className={styles.chatBubbleActions} style={{opacity: showActions ? 1 : 0, pointerEvents: showActions ? 'auto' : 'none'}}>
              <button
                className={styles.chatBubbleActionBtn}
                title="Copy message"
                onClick={handleCopy}
              >
                <Copy size={15} />
              </button>
              <ProtectedAction feature="ai_query">
              <button
                className={styles.chatBubbleActionBtn}
                title="Edit message"
                onClick={() => onEdit && onEdit(message)}
              >
                <Edit size={15} />
              </button>
              </ProtectedAction>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatInputArea({
  value,
  onChange,
  onSend,
  onCancel,
  disabled,
  isEditing,
  inputRef,
  containerClassName,
  agentMode,
  onAgentModeChange,
}) {
  const textareaRef = useRef(null);

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 400) + 'px';
    }
  };

  // Use the provided ref or our internal ref
  const actualRef = inputRef || textareaRef;

  // Adjust height when value changes
  React.useEffect(() => {
    adjustTextareaHeight();
  }, [value]);

  return (
    <div className={`${styles.chatInputContainer} ${containerClassName || ''}`}>
      {/* Main text input box */}
      <div className={styles.chatInputMainBox}>
        <textarea
          ref={actualRef}
          value={value}
          onChange={(e) => {
            onChange(e);
            adjustTextareaHeight();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            } else if (isEditing && e.key === 'Escape') {
              onCancel && onCancel();
            }
          }}
          placeholder={isEditing ? 'Edit your message...' : 'Ask Foundry AI...'}
          className={styles.chatInput}
          rows={1}
          disabled={disabled}
          autoFocus={isEditing}
        />
      </div>

      {/* Bottom control box (integrated with main box via borderless top) */}
      <div className={styles.chatInputControlBox}>
        {/* Left side: Agent mode dropdown (only show if not editing and agentMode provided) */}
        {!isEditing && agentMode && onAgentModeChange && (
          <div className={styles.chatInputControlLeft}>
            <AgentModeDropdown
              selectedMode={agentMode}
              onModeChange={onAgentModeChange}
              disabled={disabled}
            />
          </div>
        )}
        
        {/* In edit mode, show cancel button on the left */}
        {isEditing && (
          <div className={styles.chatInputControlLeft}>
            <button
              onClick={onCancel}
              className={styles.editCancelBtn}
              title="Cancel edit"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Right side: Action buttons */}
        <div className={styles.chatInputControlRight}>
        {isEditing ? (
          <ProtectedAction feature="ai_query">
            <button
              onClick={onSend}
              disabled={!value.trim()}
              className={styles.editSubmitBtn}
              title="Save changes"
            >
              <ArrowUp size={16} />
            </button>
          </ProtectedAction>
        ) : (
          <ProtectedAction feature="ai_query">
          <button
            onClick={onSend}
            disabled={!value.trim() || disabled}
            className={styles.chatSendBtn}
            title="Send message"
          >
            <ArrowUp size={16} />
          </button>
          </ProtectedAction>
        )}
      </div>
      </div>

      {/* Hint text below */}
      <div className={styles.chatInputHint}>
        {isEditing ? 'Press Enter to save, Escape to cancel' : 'Shift + Enter for new line'}
      </div>
    </div>
  );
}

export default function RightChatPanel({ 
  collapsed, 
  onHistoryClick, 
  onDragStart,
  className = '',
  style = {}
}) {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuthContext();
  
  
  // Get chat state and actions from context
  const {
    dialogId,
    conversationId,
    messages,
    loading: chatLoading,
    error: chatError,
    agentMode,
    setAgentMode,
    loadDialog,
    createNewDialog,
    sendMessage,
    refreshDialogList,
    setMessages
  } = useChatContext();

  // Use our simplified hook to check document status
  const { noDocs, anyInProgress, loading: statusLoading } = useDialogDocStatus();

  // Chat state
  const [userInput, setUserInput] = useState('');
  const [showNewDialogModal, setShowNewDialogModal] = useState(false);
  const [isCreatingNewDialog, setIsCreatingNewDialog] = useState(false);
  // Add these to track previous values
  const prevDialogIdRef = useRef(null);
  const prevConversationIdRef = useRef(null);
  
  // State to store the project ID for the new dialog
  const [projectIdForNewDialog, setProjectIdForNewDialog] = useState(null);

  // State for alert dismissal
  const [alertDismissed, setAlertDismissed] = useState(false);
  
  // State to track if the input is focused
  const [inputFocused, setInputFocused] = useState(false);

  // Edit message state
  const [editingMessage, setEditingMessage] = useState(null);
  const [editInput, setEditInput] = useState('');
  const editInputRef = useRef(null);

  // Reset alert dismissal when dialog changes
  useEffect(() => {
    if (dialogId !== prevDialogIdRef.current) {
      setAlertDismissed(false);
      prevDialogIdRef.current = dialogId;
    }
  }, [dialogId]);

  // Check if any message is currently loading (for showing the animation)
  const isAnyMessageLoading = messages.some(message => message.loading === true);
  
  // Check if there's a loading message with no content (show "Just a moment...")
  const isWaitingForResponse = messages.some(message => message.loading === true && (!message.content || message.content.trim() === ''));
  
  // Check if there's a loading message with content (show typing effect)
  const isTypingResponse = messages.some(message => message.loading === true && message.content && message.content.trim() !== '');

  // Directly calculate warning message based on document status
  const docStatusWarning = useMemo(() => {
    // Don't show warnings if we're still loading or if there's no active dialog
    if (statusLoading || !dialogId) {
      return null;
    }
    
    // Debug logging
    console.debug('[DocStatus] noDocs:', noDocs, 'anyInProgress:', anyInProgress, 'dialogId:', dialogId);
    
    if (noDocs) {
      return "No documents available. Upload documents to get AI responses specific to your data.";
    } else if (anyInProgress) {
      return "Documents are being processed. For AI responses that include your documents, please wait until processing completes.";
    }
    
    return null;
  }, [noDocs, anyInProgress, statusLoading, dialogId]);
  
  // Drag to resize
  const panelRef = useRef(null);
  // Give it a suitable default/min/max
  const { handleMouseDown } = useDragWidth(panelRef, {
    defaultWidth: 425,
    minWidth: 300,
    maxWidth: 600, // CHANGED FROM 800 TO 600
    invertDrag: true,
  });

  // Auto-scroll
  const chatEndRef = useRef(null);
  const isStreamingRef = useRef(false);
  
  function scrollToBottom(smooth = true) {
    chatEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }
  const warningRef = useRef(null);

  // Track if we're currently streaming (any message is loading with content)
  useEffect(() => {
    const hasStreamingMessage = messages.some(m => m.loading && m.content && m.content.trim() !== '');
    isStreamingRef.current = hasStreamingMessage;
  }, [messages]);

  // Auto-scroll when messages change
  // Use instant scroll during streaming to avoid glitchy buzz effect
  useEffect(() => {
    // If streaming, use instant scroll; otherwise smooth scroll
    scrollToBottom(!isStreamingRef.current);
  }, [messages]);

  // Called when a new dialog is created by the modal
  async function handleDialogCreated({ dialogId: newId, conversationId: cId }) {
    console.debug(`[RightChatPanel] New dialog created: Dialog=${newId}, Conv=${cId}`);
    // Just load the dialog in ChatContext (single source of truth)
    await loadDialog(newId, cId);
    
    setShowNewDialogModal(false);
    setIsCreatingNewDialog(false);
  }

  // Add a new function to handle the plus icon click
  async function handleNewDialogClick() {
    if (!dialogId) {
      console.error("Cannot create new dialog: No active dialog ID found.");
      return;
    }
    setIsCreatingNewDialog(true);
    setProjectIdForNewDialog(null);
    try {
      const cachedDetails = await getLocalDialogDetails(dialogId);
      
      if (cachedDetails && cachedDetails.project_id) {
        console.debug(`Using cached project_id: ${cachedDetails.project_id} for new dialog creation`);
        setProjectIdForNewDialog(cachedDetails.project_id);
        setShowNewDialogModal(true);
      } else {
        const currentDialogDetails = await getDialogDetails(dialogId);
        if (currentDialogDetails && currentDialogDetails.project_id) {
          console.debug(`Creating new dialog under project: ${currentDialogDetails.project_id}`);
          setProjectIdForNewDialog(currentDialogDetails.project_id);
        } else {
          console.warn(`Current dialog ${dialogId} has no associated project_id. Creating dialog without project context.`);
        }
        setShowNewDialogModal(true);
      }
    } catch (error) {
      console.error("Error fetching current dialog details:", error);
      console.warn("Proceeding to create dialog without project context due to error.");
      setShowNewDialogModal(true); 
      setIsCreatingNewDialog(false);
    }
  }

  // Handle sending a message
  async function handleSend() {
    const trimmed = userInput.trim();
    if (!trimmed) return;
    
    // Clear input immediately for better UX
    setUserInput('');
    
    // Use the sendMessage function from context
    await sendMessage(trimmed);
  }

  // Handle drag start
  const handleDragStart = (e) => {
    handleMouseDown(e);
  };

  function handleInputFocus() {
    // Focus styling is handled by CSS :focus pseudo-class
    setInputFocused(true);
    // Reset dismissal state when focusing input so banner shows if still needed
    setAlertDismissed(false);
  }

  function handleInputBlur() {
    // Blur styling is handled by CSS
    setInputFocused(false);
    // Keep dismissal state as-is when input loses focus
  }

  // Edit message handlers
  const handleStartEdit = (message) => {
    setEditingMessage(message);
    setEditInput(message.content);
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditInput('');
  };

  const handleSubmitEdit = async () => {
    if (!editInput.trim() || !editingMessage) return;
    try {
      // Find the index of the message being edited
      const editIdx = messages.findIndex(msg => msg.id === editingMessage.id);
      if (editIdx === -1) return;
      // Only keep messages up to (but not including) the edited message
      const updatedMessages = messages.slice(0, editIdx);
      if (typeof setMessages === 'function') setMessages(updatedMessages);
      setEditingMessage(null);
      setEditInput('');
      await sendMessage(editInput.trim());
    } catch (error) {
      console.error('Failed to edit message:', error);
    }
  };


  // If fully collapsed, don't render (but allow mobile drawer to override)
  // Check if we have mobile drawer classes applied
  const isMobileDrawer = className && className.includes('mobile-drawer');
  
  if (collapsed && !isMobileDrawer) {
    return null;
  }

  return (
    <>
      <div 
        ref={panelRef}
        className={`${styles.rightChatPanel} ${className}`}
        style={style}
      >
        {/* Drag handle */}
        <div
          className={styles.chatDragHandle}
          onMouseDown={handleDragStart}
          onDragStart={onDragStart}
        />

        {/* Header */}
        <div className={styles.chatPanelHeader}>
          <div className={styles.chatHeaderContent}>
            <div className={styles.chatHeaderActions}>
              <button
                onClick={handleNewDialogClick}
                className={styles.chatHeaderBtn}
                disabled={isCreatingNewDialog}
                title="New conversation"
              >
                {isCreatingNewDialog ? (
                  <div className={styles.chatBtnSpinner} />
                ) : (
                  <Plus size={22} />
                )}
              </button>
              
              <button
                onClick={onHistoryClick}
                className={styles.chatHeaderBtn}
                title="Chat history"
              >
                <MessageSquare size={22} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {/* Messages area */}
            <div className={styles.chatMessagesContainer}>
              <div className={styles.chatMessagesArea}>
                {/* Loading indicator - shows when dialog is being loaded initially */}
                {chatLoading && messages.length === 0 && (
                  <div className={`${styles.chatMessage} ${styles.chatMessageAssistant}`}>
                    <div className={styles.chatMessageText}>
                      <div className={styles.chatLoading}>
                        <span className={styles.chatLoadingText}>Just a moment</span>
                        <div className={styles.chatLoadingDots}>
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {messages
                  .filter(message => !message.loading)
                  .map((message, idx) => (
                    editingMessage === message ? (
                      <div key={message.id} style={{ width: '100%' }}>
                        <ChatInputArea
                          value={editInput}
                          onChange={e => setEditInput(e.target.value)}
                          onSend={handleSubmitEdit}
                          onCancel={handleCancelEdit}
                          disabled={isAnyMessageLoading}
                          isEditing
                          inputRef={editInputRef}
                          containerClassName={styles.ephemeralEditInput}
                          agentMode={null}
                          onAgentModeChange={null}
                        />
                      </div>
                    ) : (
                      <ChatMessage 
                        key={message.id} 
                        message={message} 
                        onEdit={handleStartEdit}
                        onCopy={null}
                        isEditing={editingMessage === message}
                        editInput={editInput}
                        setEditInput={setEditInput}
                        onEditSubmit={handleSubmitEdit}
                        onEditCancel={handleCancelEdit}
                      />
                    )
                  ))}
                
                {/* Loading indicator - shows when waiting for response to start */}
                {isWaitingForResponse && (
                  <div className={`${styles.chatMessage} ${styles.chatMessageAssistant}`}>
                    <div className={styles.chatMessageText}>
                      <div className={styles.chatLoading}>
                        <span className={styles.chatLoadingText}>Just a moment</span>
                        <div className={styles.chatLoadingDots}>
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Typing response - shows actual content while loading */}
                {isTypingResponse && messages
                  .filter(message => message.loading === true && message.content && message.content.trim() !== '')
                  .map(message => (
                    <ChatMessage 
                      key={message.id} 
                      message={message} 
                      onEdit={handleStartEdit}
                      onCopy={null}
                      isEditing={editingMessage === message}
                      editInput={editInput}
                      setEditInput={setEditInput}
                      onEditSubmit={handleSubmitEdit}
                      onEditCancel={handleCancelEdit}
                    />
                  ))}
                
                {/* Error message */}
                {chatError && (
                  <div className={styles.chatError}>
                    Failed to send message. Please try again.
                  </div>
                )}
                
                <div ref={chatEndRef} />
              </div>
            </div>

            {/* Permanent input area for new messages (not affected by edit mode) */}
            <ChatInputArea
              value={userInput}
              onChange={e => setUserInput(e.target.value)}
              onSend={handleSend}
              disabled={isAnyMessageLoading}
              isEditing={false}
              inputRef={null}
              containerClassName={styles.permanentInput}
              agentMode={agentMode}
              onAgentModeChange={setAgentMode}
            />
      </div>

      {/* New Dialog Modal */}
      {showNewDialogModal && (
        <NewDialogModal
          onClose={() => setShowNewDialogModal(false)}
          onDialogCreated={handleDialogCreated}
          projectId={projectIdForNewDialog}
        />
      )}

      {/* Top-right alert */}
      {docStatusWarning && !alertDismissed && (
        <Banner
          type="warning"
          title="Document Status"
          message={docStatusWarning}
          position="top-right"
          autoHide={false}
          onClose={() => setAlertDismissed(true)}
        />
      )}
    </>
  );
}










